import React, { memo, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Icon, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import CampaignSection from './CampaignSection';
import TitleInput from './TitleInput';
import SenderForm from './SenderForm';
import RecipientsForm from './RecipientsForm';
import SenderFormModal from '../SenderFormModal';
import './styles.scss';

const MOCK_CAMPAIGN = {
    id: 1,
    title: '',
    status: getLiteral('label_draft'),
    sender: {
        name: 'Lisa Matthew',
        email: 'lisa_matthew@cglobal.com',
    },
};

const STATUS_BADGE_TYPES = {
    [getLiteral('label_draft')]: 'default',
};

const MOCK_SENDERS = [
    {
        label: 'lisa_matthew@cglobal.com',
        value: '1',
        subLabel: 'Lisa Matthew',
    },
    {
        label: 'marketing@sage.com',
        value: '2',
        subLabel: 'Marketing Team',
    },
    {
        label: 'sales@company.com',
        value: '3',
        subLabel: 'Sales Department',
    },
];

const MOCK_RECIPIENTS_USAGE = {
    selectedRecipients: 0,
    maxRecipients: 20000,
    remainingEmails: 20000,
};

const CampaignDetails = memo(
    ({
        campaign = MOCK_CAMPAIGN,
        recipientsUsage: initialRecipientsUsage = MOCK_RECIPIENTS_USAGE,
        onBack,
        onPreview,
        onSend,
        onAddRecipients,
        onCancelRecipients,
        onSaveRecipients,
    }) => {
        const theme = useTheme();
        const [expandedSections, setExpandedSections] = useState({
            sender: false,
            recipients: false,
            subject: false,
            design: false,
            additionalSettings: false,
        });

        // Local state for recipients usage (fake implementation)
        const [recipientsUsage, setRecipientsUsage] = useState(initialRecipientsUsage);

        // Sender modal state
        const [isSenderModalOpen, setIsSenderModalOpen] = useState(false);
        const [senders, setSenders] = useState(MOCK_SENDERS);
        const [isCreatingSender, setIsCreatingSender] = useState(false);

        // Lifted state from SenderForm for controlled selection
        const [selectedSenderId, setSelectedSenderId] = useState(campaign.sender?.value || '');
        const [senderName, setSenderName] = useState(campaign.sender?.name || '');

        const hasExpandedSection = useMemo(() => {
            return Object.values(expandedSections).some(Boolean);
        }, [expandedSections]);

        const completedSections = {
            sender: true,
            recipients: false,
            subject: false,
            design: false,
            additionalSettings: false,
        };

        const handleToggleSection = useCallback((section) => {
            setExpandedSections((prev) => ({
                ...prev,
                [section]: !prev[section],
            }));
        }, []);

        const handleBack = useCallback(() => {
            onBack?.();
        }, [onBack]);

        const handlePreview = useCallback(() => {
            onPreview?.(campaign);
        }, [onPreview, campaign]);

        const handleSend = useCallback(() => {
            onSend?.(campaign);
        }, [onSend, campaign]);

        const handleAddRecipients = useCallback(() => {
            setRecipientsUsage((prev) => {
                const newSelected = prev.selectedRecipients + 1200;
                const newRemaining = Math.max(0, prev.remainingEmails - 1200);
                return {
                    ...prev,
                    selectedRecipients: newSelected,
                    remainingEmails: newRemaining,
                };
            });
            onAddRecipients?.(campaign);
        }, [onAddRecipients, campaign]);

        const handleCancelRecipients = useCallback(() => {
            onCancelRecipients?.(campaign);
        }, [onCancelRecipients, campaign]);

        const handleSaveRecipients = useCallback(() => {
            onSaveRecipients?.(campaign);
        }, [onSaveRecipients, campaign]);

        const handleSaveSender = useCallback(
            ({ senderId, name }) => {
                console.log('Saving sender:', { senderId, name });
                // TODO: Update campaign sender data
                handleToggleSection('sender');
            },
            [handleToggleSection],
        );

        // Sender modal handlers
        const handleOpenSenderModal = useCallback(() => {
            setIsSenderModalOpen(true);
        }, []);

        const handleCloseSenderModal = useCallback(() => {
            setIsSenderModalOpen(false);
        }, []);

        const handleCreateSender = useCallback(
            async (formData) => {
                setIsCreatingSender(true);
                try {
                    // Mock service call - replace with actual API call when available
                    // const newSender = await senderService.create(formData);

                    // Simulate API delay
                    await new Promise((resolve) => setTimeout(resolve, 500));

                    // Create new sender object matching the format
                    const newSender = {
                        label: formData.fromEmail,
                        value: Date.now().toString(),
                        subLabel: formData.fromName,
                    };

                    // Update senders list
                    setSenders((prev) => [...prev, newSender]);

                    // Automatically select the new sender
                    setSelectedSenderId(newSender.value);
                    setSenderName(formData.fromName);

                    handleCloseSenderModal();
                } catch (error) {
                    console.error('Failed to create sender:', error);
                    // TODO: Show error notification
                } finally {
                    setIsCreatingSender(false);
                }
            },
            [handleCloseSenderModal],
        );

        // Handler for when sender selection changes in SenderForm
        const handleSenderSelectionChange = useCallback((senderId) => {
            setSelectedSenderId(senderId);
        }, []);

        // Handler for when sender name changes in SenderForm
        const handleSenderNameChange = useCallback((name) => {
            setSenderName(name);
        }, []);

        const badgeType = STATUS_BADGE_TYPES[campaign.status] || 'default';

        return (
            <div className="campaign-details">
                <div className="campaign-details__header">
                    <div className="campaign-details__header-left">
                        <div
                            className="campaign-details__back-button"
                            onClick={handleBack}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleBack()}
                        >
                            <Icon name="arrowBack" />
                        </div>
                        <TitleInput
                            title={campaign.title}
                            onSave={(newTitle) => console.log('Save:', newTitle)}
                        />
                        <Badge type={badgeType}>{campaign.status}</Badge>
                    </div>
                    <div className="campaign-details__header-actions">
                        <Button type="secondary" size="small" onClick={handlePreview} isDisabled>
                            {getLiteral('action_preview')}
                        </Button>
                        <Button type="primary" size="small" isDisabled onClick={handleSend}>
                            {getLiteral('action_send')}
                        </Button>
                    </div>
                </div>

                <div
                    className={[
                        'campaign-details__container',
                        hasExpandedSection ? 'campaign-details__container--expanded' : '',
                    ].join(' ')}
                >
                    <div className="campaign-details__content">
                        <CampaignSection
                            isCompleted={completedSections.sender}
                            title={getLiteral('label_sender')}
                            subtitle={
                                !!campaign.sender && !expandedSections.sender ? (
                                    <div className="campaign-details__section-subtitle">
                                        <Text type="captionMedium">{`${campaign.sender.name}`}</Text>
                                        <Text
                                            type="caption"
                                            color={theme.colors.textLight.secondary}
                                        >{`• ${campaign.sender.email}`}</Text>
                                    </div>
                                ) : (
                                    getLiteral(
                                        'label_marketing_campaigns_who_is_sending_this_email_campaign',
                                    )
                                )
                            }
                            actionLabel={getLiteral('action_manage_sender')}
                            isExpanded={expandedSections.sender}
                            onToggle={() => handleToggleSection('sender')}
                            isDimmed={hasExpandedSection && !expandedSections.sender}
                        >
                            <SenderForm
                                senders={senders}
                                selectedSenderId={selectedSenderId}
                                senderName={senderName}
                                onSave={handleSaveSender}
                                onCancel={() => handleToggleSection('sender')}
                                onAddNewSender={handleOpenSenderModal}
                                onSelectedSenderChange={handleSenderSelectionChange}
                                onSenderNameChange={handleSenderNameChange}
                            />
                        </CampaignSection>

                        <CampaignSection
                            isCompleted={completedSections.recipients}
                            title={getLiteral('label_recipients')}
                            subtitle={getLiteral(
                                'label_marketing_campaigns_recipients_campaign_desc',
                            )}
                            actionLabel={getLiteral(
                                recipientsUsage.selectedRecipients > 0
                                    ? 'action_manage_recipients'
                                    : 'action_add_recipients',
                            )}
                            isExpanded={expandedSections.recipients}
                            onToggle={() => handleToggleSection('recipients')}
                            isDimmed={hasExpandedSection && !expandedSections.recipients}
                        >
                            <RecipientsForm
                                selectedRecipients={recipientsUsage.selectedRecipients}
                                maxRecipients={recipientsUsage.maxRecipients}
                                remainingEmails={recipientsUsage.remainingEmails}
                                onAddRecipients={handleAddRecipients}
                                onCancel={handleCancelRecipients}
                                onSave={handleSaveRecipients}
                            />
                        </CampaignSection>

                        <CampaignSection
                            isCompleted={completedSections.subject}
                            title={getLiteral('label_subject')}
                            subtitle={getLiteral('label_marketing_campaigns_subject_campaign_desc')}
                            actionLabel={getLiteral('action_add_subject')}
                            isExpanded={expandedSections.subject}
                            onToggle={() => handleToggleSection('subject')}
                            isDimmed={hasExpandedSection && !expandedSections.subject}
                        >
                            <Text type="body" className="campaign-details__placeholder">
                                Subject input placeholder
                            </Text>
                        </CampaignSection>

                        <CampaignSection
                            isCompleted={completedSections.design}
                            title={getLiteral('label_design')}
                            subtitle={getLiteral('label_marketing_campaigns_design_campaign_desc')}
                            actionLabel={getLiteral('action_start_designing')}
                            isExpanded={expandedSections.design}
                            onToggle={() => handleToggleSection('design')}
                            isDimmed={hasExpandedSection && !expandedSections.design}
                        >
                            <Text type="body" className="campaign-details__placeholder">
                                Email design editor placeholder
                            </Text>
                        </CampaignSection>

                        <CampaignSection
                            isCompleted={completedSections.additionalSettings}
                            title={getLiteral('label_additional_settings')}
                            subtitle=""
                            actionLabel={getLiteral('action_modify_settings')}
                            isExpanded={expandedSections.additionalSettings}
                            onToggle={() => handleToggleSection('additionalSettings')}
                            hideStatusIcon
                            isDimmed={hasExpandedSection && !expandedSections.additionalSettings}
                        >
                            <Text type="body" className="campaign-details__placeholder">
                                Additional settings form placeholder
                            </Text>
                        </CampaignSection>
                    </div>
                </div>

                <SenderFormModal
                    isOpen={isSenderModalOpen}
                    onCancel={handleCloseSenderModal}
                    onCreate={handleCreateSender}
                    onRequestClose={handleCloseSenderModal}
                />
            </div>
        );
    },
);

CampaignDetails.displayName = 'CampaignDetails';

CampaignDetails.propTypes = {
    campaign: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        status: PropTypes.oneOf(['Draft', 'Scheduled', 'Sent']),
        sender: PropTypes.shape({
            name: PropTypes.string,
            email: PropTypes.string,
        }),
    }),
    recipientsUsage: PropTypes.shape({
        selectedRecipients: PropTypes.number,
        maxRecipients: PropTypes.number,
        remainingEmails: PropTypes.number,
    }),
    onBack: PropTypes.func,
    onPreview: PropTypes.func,
    onSend: PropTypes.func,
    onAddRecipients: PropTypes.func,
    onCancelRecipients: PropTypes.func,
    onSaveRecipients: PropTypes.func,
};

export default CampaignDetails;
